/**
 * Service pour gérer les liens Google Drive et télécharger les images
 */

class GoogleDriveService {

  static PLACEHOLDER_BLACKLIST = new Set([
    'example_id', 'example', 'file_id', 'xxxxxxxxxxxxxxxxxxxxxxxxx',
    '1234567890abcdefghijklmnop', '0000000000000000000000000', 'test',
    'test1', 'exemple', 'exemple1', 'demo', 'placeholder', 'invalid', 'sample', 'fake', 'id'
  ]);

  static isPlaceholderId(id) {
    if (!id) return true;
    if (id.length < 10) return true;
    const lower = String(id).trim().toLowerCase();
    if (GoogleDriveService.PLACEHOLDER_BLACKLIST.has(lower)) return true;
    if (/^(?:example|exemple|test|demo|placeholder|invalid|sample|fake|id)$/i.test(lower)) return true;
    return false;
  }

  extractFileId(url) {
    try {
      if (!url || typeof url !== 'string') return null;
      const clean = url.trim();
      const patterns = [
        /\/file\/d\/([a-zA-Z0-9_-]+)/,
        /[?&]id=([a-zA-Z0-9_-]+)/,
        /^([a-zA-Z0-9_-]{25,})$/
      ];
      for (const pattern of patterns) {
        const match = clean.match(pattern);
        if (match) return match[1];
      }
      const anyId = clean.match(/([a-zA-Z0-9_-]{25,})/);
      return anyId ? anyId[1] : null;
    } catch (error) {
      return null;
    }
  }

  isGoogleDriveUrl(url) {
    if (!url || typeof url !== 'string') return false;
    const cleanUrl = url.trim();
    const lower = cleanUrl.toLowerCase();
    if (lower.includes('drive.google.com') || lower.includes('docs.google.com')) return true;
    // URL/ID simple
    if (/^[a-zA-Z0-9_-]{25,}$/.test(cleanUrl)) {
      const id = cleanUrl;
      if (GoogleDriveService.isPlaceholderId(id)) return false;
      return true;
    }
    return false;
  }

  isPlaceholderOrExampleUrl(url) {
    if (!url || typeof url !== 'string') return true;
    const clean = url.trim();
    if (!clean) return true;
    // URLs explicitement exemple / placeholder
    if (/example\.com|example\.org|sample|placeholder|demo_image|demo\.|test1?\.|exemple/i.test(clean)) {
      return true;
    }
    const id = this.extractFileId(clean);
    if (id && GoogleDriveService.isPlaceholderId(id)) return true;
    // Si l'URL contient EXAMPLE_ID, FILE_ID textuellement
    if (/(example_id|file_id)/i.test(clean)) return true;
    return false;
  }

  convertToDirectDownloadUrl(driveUrl) {
    if (!driveUrl || typeof driveUrl !== 'string') {
      throw new Error('URL Google Drive invalide');
    }
    const cleanUrl = driveUrl.trim();

    const viewPattern = /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view/;
    const viewMatch = cleanUrl.match(viewPattern);
    if (viewMatch) {
      const fileId = viewMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    const openPattern = /https:\/\/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/;
    const openMatch = cleanUrl.match(openPattern);
    if (openMatch) {
      const fileId = openMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    const idPattern = /^[a-zA-Z0-9_-]{25,}$/;
    if (idPattern.test(cleanUrl)) {
      return `https://drive.google.com/uc?export=download&id=${cleanUrl}`;
    }

    if (cleanUrl.includes('drive.google.com/uc?export=download')) {
      return cleanUrl;
    }

    const anyIdMatch = cleanUrl.match(/([a-zA-Z0-9_-]{25,})/);
    if (anyIdMatch) {
      const fileId = anyIdMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    throw new Error(`Format d'URL Google Drive non reconnu: ${cleanUrl}`);
  }

  async downloadImage(driveUrl, filename = null) {
    try {
      if (this.isPlaceholderOrExampleUrl(driveUrl)) {
        return {
          success: false,
          skipped: true,
          error: 'URL exemple / placeholder ignorée',
          originalUrl: driveUrl
        };
      }
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/products/download-google-drive-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ url: driveUrl })
      });

      let errorData = {};
      try { errorData = await response.json(); } catch (_) {}

      if (!response.ok) {
        const msg = errorData?.message || `Erreur HTTP ${response.status}`;
        // 400 = entrée invalide (placeholder, droit insuffisant, etc) — on retourne sans throw au niveau global
        return {
          success: false,
          error: msg,
          statusCode: response.status,
          originalUrl: driveUrl
        };
      }

      const result = errorData;

      if (!result || !result.success) {
        return {
          success: false,
          error: result?.message || 'Erreur lors du téléchargement',
          originalUrl: driveUrl
        };
      }

      return {
        success: true,
        filepath: result.filepath,
        filename: result.filename,
        originalUrl: result.originalUrl || driveUrl
      };

    } catch (error) {
      console.error('Erreur téléchargement Google Drive:', error);
      return {
        success: false,
        error: error.message || 'Erreur réseau',
        originalUrl: driveUrl
      };
    }
  }

  /**
   * Télécharger un fichier (PDF/doc) via le backend
   */
  async downloadFile(driveUrl) {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/products/download-google-drive-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ url: driveUrl })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  /**
   * Traiter plusieurs liens Google Drive en parallèle
   * @param {Array<string>} driveUrls - Liste des URLs Google Drive
   * @param {Function} onProgress - Callback de progression
   * @returns {Promise<Array<{success: boolean, filepath?: string, error?: string, originalUrl: string}>>}
   */
  async downloadMultipleImages(driveUrls, onProgress = null) {
    const total = driveUrls.length;

    const batchSize = 5;
    const results = [];

    for (let i = 0; i < driveUrls.length; i += batchSize) {
      const batch = driveUrls.slice(i, i + batchSize);

      const batchPromises = batch.map(async (url, index) => {
        const globalIndex = i + index;

        try {
          if (this.isPlaceholderOrExampleUrl(url)) {
            return {
              success: false,
              skipped: true,
              error: 'URL exemple / placeholder ignorée',
              originalUrl: url
            };
          }

          if (!this.isGoogleDriveUrl(url)) {
            return {
              success: false,
              error: 'URL Google Drive invalide',
              originalUrl: url
            };
          }

          const result = await this.downloadImage(url);

          if (result.success) {
            return {
              success: true,
              filepath: result.filepath,
              filename: result.filename,
              originalUrl: url
            };
          } else {
            if (!result.skipped) console.error(`❌ Erreur image ${globalIndex + 1}:`, result.error);
            return {
              success: false,
              skipped: !!result.skipped,
              error: result.error,
              originalUrl: url
            };
          }

        } catch (error) {
          console.error(`❌ Erreur image ${globalIndex + 1}:`, error);
          return {
            success: false,
            error: error.message,
            originalUrl: url
          };
        }
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      if (onProgress) {
        const progress = Math.round(((i + batch.length) / total) * 100);
        onProgress(progress, i + batch.length, total);
      }

      if (i + batchSize < driveUrls.length) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    return results;
  }

  /**
   * Valider et préparer une URL Google Drive
   * @param {string} url - URL à valider
   * @returns {Object} {isValid: boolean, directUrl?: string, error?: string}
   */
  validateAndPrepareUrl(url) {
    try {
      if (!this.isGoogleDriveUrl(url)) {
        return {
          isValid: false,
          error: 'Ce n\'est pas un lien Google Drive valide'
        };
      }

      const directUrl = this.convertToDirectDownloadUrl(url);
      
      return {
        isValid: true,
        directUrl: directUrl
      };

    } catch (error) {
      return {
        isValid: false,
        error: error.message
      };
    }
  }

  /**
   * Extraire l'ID du fichier depuis une URL Google Drive
   * @param {string} url - URL Google Drive
   * @returns {string|null} ID du fichier ou null si non trouvé
   */
  extractFileId(url) {
    try {
      const patterns = [
        /\/file\/d\/([a-zA-Z0-9_-]+)/,
        /[?&]id=([a-zA-Z0-9_-]+)/,
        /^([a-zA-Z0-9_-]{25,})$/
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          return match[1];
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }
}

// Instance singleton
export const googleDriveService = new GoogleDriveService();
export default googleDriveService;
